import {
  Box,
  Card,
  CardBody,
  Flex,
  FormControl,
  Grid,
  Heading,
  Stack,
  theme,
  Tooltip,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { FaRegCircleDot } from "react-icons/fa6";
import { TbUsers } from "react-icons/tb";
import { fetchController } from "../../utils/FetchController/fetchController";
import Loader from "../../utils/Loader/Loader";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import FormLabelWithInfoTooltip from "../Widgets/FormLabelWithInfoTooltip";
import AgentChatWidget from "./AgentChatWidget";
import LiveUsers from "./LiveUsers";
import SelectAssistant from "./SelectAssistant";
import useLiveAgentSocket from "./useLiveAgentSocket";

export default function LiveAgentChats() {
  const [assistant, setAssistant] = useState();
  const [activeUsers, setActiveUsers] = useState({});

  const [loading, setLoading] = useState(false);
  const [dropdownDisable, setDropdownDisable] = useState(false);

  const checkBillingStatus = async () => {
    const baseUrl = getHostDomain();
    setLoading(true);
    const userActiveBillingPlanId = localStorage.getItem(
      "current_active_billing_plan_id"
    );

    if (userActiveBillingPlanId) {
      const response = await fetchController(
        baseUrl + `/api/v1/billing/billing_plan/${userActiveBillingPlanId}`,
        "GET"
      );
      const userBillingPlan = response?.data;
      setDropdownDisable((userBillingPlan.credits.human_agents || 0) <= 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    checkBillingStatus();
  }, []);

  const updateUserMessages = ({ user, message }) => {
    setActiveUsers((prev) => ({
      ...prev,
      [user]: {
        ...prev[user],
        messages: [...prev[user].messages, message],
        lastMessageAt: new Date(),
      },
    }));
  };

  const onSetActiveUsersList = (response) => {
    setActiveUsers(
      response.users.reduce(
        (activeUserMessageMap, userId) => ({
          ...activeUserMessageMap,
          [userId]: {
            messages: [],
            accepted: false,
            active: true,
            channel: response.channels?.[userId] || "web",
            contactName: response.user_info?.[userId]?.contact_name || "",
            lastMessageAt: null,
          },
        }),
        {}
      )
    );
  };
  const onUserDeactivate = (response) => {
    setActiveUsers((prev) => ({
      ...prev,
      [response.user_id]: {
        ...prev[response.user_id],
        active: false,
      },
    }));
  };
  const onAddNewActiveUser = async (response) => {
    setActiveUsers((prev) => ({
      ...prev,
      [response.user_id]: {
        messages: response.chat_history.map((chat) => ({
          sentBy: chat.role,
          message: chat.content,
        })),
        accepted: false,
        active: true,
        channel: response.channel || "web",
        contactName: response.contact_name || "",
        lastMessageAt: new Date(),
      },
    }));
    const tone = new Audio("/new-user-tone.mp3");
    await tone.play();
  };
  const onAddMessagesToActiveUser = async (response) => {
    setActiveUsers((prev) => ({
      ...prev,
      [response.user_id]: {
        ...prev[response.user_id],
        messages: [
          ...prev[response.user_id].messages,
          ...response.chat_history.map((chat) => ({
            sentBy: chat.role,
            message: chat.content,
          })),
        ],
      },
    }));
  };
  const onAddReceivedMessage = (response) => {
    if (!currentUser && !response.user_id) return;

    const user_id = response.user_id || currentUser.user;
    setActiveUsers((prev) => ({
      ...prev,
      [user_id]: {
        ...prev[user_id],
        messages: [
          ...prev[user_id].messages,
          { message: response.message, sentBy: user_id },
        ],
        lastMessageAt: new Date(),
      },
    }));
  };
  const onConversationClosed = (response) => {
    const closedUserId = response.user_id;
    setActiveUsers((prev) => {
      const next = { ...prev };
      delete next[closedUserId];
      return next;
    });
    if (currentUser?.user === closedUserId) setCurrentUser(null);
  };

  const { status, acceptNewUser, sendMessage, closeConversation } = useLiveAgentSocket({
    assistant,
    onSetActiveUsersList,
    onAddNewActiveUser,
    onAddReceivedMessage,
    updateUserMessages,
    onUserDeactivate,
    onAddMessagesToActiveUser,
    onConversationClosed,
  });
  const onSetCurrentUser = (newUserItem) => {
    setCurrentUser(newUserItem);
    if (!activeUsers[newUserItem.user]?.accepted) {
      acceptNewUser({
        user: newUserItem.user,
      });
      setActiveUsers({
        ...activeUsers,
        [newUserItem.user]: {
          ...activeUsers[newUserItem.user],
          accepted: true,
        },
      });
    }
  };
  const connected = status === "connected";
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserProps = {
    currentUser,
    onSetCurrentUser,
  };
  const messageList =
    currentUser && activeUsers[currentUser.user]
      ? activeUsers[currentUser.user].messages
      : [];
  const sendMessageToUser = (userId) => (message) => {
    sendMessage({
      user_id: userId,
      message,
    });
    setActiveUsers((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        messages: [...prev[userId].messages, { message, sentBy: "agent" }],
        lastMessageAt: new Date(),
      },
    }));
  };
  const users = Object.keys(activeUsers);
  const chatWindowHeading = users.indexOf(currentUser?.user) + 1;
  const agency = useContext(AgencyContext);
  const { myDetails, textColor, colorPreset, buttonColorScheme } = agency;

  const chatBg = useColorModeValue("#E8E8E8", theme.colors.gray[700]);
  const chatTextColor = useColorModeValue("black", "white");
  const selectedChatBg = colorPreset || "#3182ce";
  const agencyLogoUrl = myDetails?.branding?.logo_url;

  const toolTipMsg =
    myDetails && myDetails?.agency_id
      ? `Please contact ${myDetails?.branding?.agency_name || ""} `
      : "Please upgrade to platinum or above";
      
  return (
      <VStack
        h="100%"
        w={"100%"}
        spacing={2}
        justifyContent={"space-between"}
        borderRadius="md"
      >
        <Flex
          direction={"column"}
          gap={3}
          h={"100%"}
          w={"100%"}
          overflowY={"auto"}
        >
          <Flex gap={2} w={"100%"} alignItems="center" py={2}>
            <Heading color={textColor} fontSize="xl">
              Human Agents
            </Heading>
          </Flex>

          {loading ? (
            <Loader />
          ) : (
            <>
              <Card variant={"elevated"} w={"100%"}>
                <CardBody>
                  <Tooltip
                    isDisabled={!dropdownDisable}
                    label={toolTipMsg}
                    placement="bottom-start"
                  >
                    <FormControl cursor={"pointer"}>
                      <FormLabelWithInfoTooltip
                        label="Select Assistant check"
                        tip="Enable Human Handover for the Assistant to allow you & other human agents to chat directly with your users."
                      />
                      <SelectAssistant
                        assistant={assistant}
                        onChangeAssistant={({ value }) => {
                          setAssistant(value);
                        }}
                        disable={dropdownDisable}
                      />
                    </FormControl>
                  </Tooltip>
                </CardBody>
              </Card>

              {assistant ? (
                <Stack marginBlock={4} spacing={2}>
                  <Flex
                    gap={3}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                  >
                    <Heading fontSize={"xl"}>
                      {" "}
                      Live Human Agent Conversations
                    </Heading>
                    <FaRegCircleDot
                      title={connected ? "Connected" : "Not connected"}
                      size={20}
                      color={connected ? "green" : "red"}
                    />
                  </Flex>
                  {Object.keys(activeUsers).length ? (
                    <Grid
                      marginBlock={2}
                      gap={3}
                      gridTemplateColumns={"300px 1fr"}
                    >
                      <Box maxH={"70svh"} overflowY={"auto"}>
                        <LiveUsers
                          activeUsers={activeUsers}
                          currentUserProps={currentUserProps}
                          selectedChatBg={selectedChatBg}
                          chatBg={chatBg}
                          chatTextColor={chatTextColor}
                        />
                      </Box>
                      {currentUser ? (
                        <Box paddingLeft={3} borderLeft={"1px solid lightgray"}>
                          <AgentChatWidget
                            messages={messageList}
                            chatWindowHeading={chatWindowHeading}
                            connected={connected}
                            sendMessage={sendMessageToUser(currentUser.user)}
                            closeConversation={() => closeConversation({ user_id: currentUser.user })}
                            selectedChatBg={selectedChatBg}
                            chatBg={chatBg}
                            chatTextColor={chatTextColor}
                            agencyLogoUrl={agencyLogoUrl}
                            buttonColorScheme={buttonColorScheme}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                  ) : (
                    <Flex
                      gap={8}
                      alignItems={"center"}
                      justifyContent={"center"}
                      flexDir={"column"}
                    >
                      <Box p={3} bg={"white"} borderRadius={"full"}>
                        <TbUsers size={50} />
                      </Box>
                      <Heading fontSize={"md"}>No users found</Heading>
                    </Flex>
                  )}
                </Stack>
              ) : null}
            </>
          )}
        </Flex>
      </VStack>
  );
}
